import { BadgeCheck, CalendarClock, ClipboardList, FolderOpen, IdCard, ShieldCheck, Target, Users } from 'lucide-react';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { SettingsMenu, type SettingsMenuEntry } from '../../../../components/settings/SettingsMenu';
import { LEARNING_DEVELOPMENT_CATALOGS } from '../../learning-development/learningDevelopmentTypes';

/*
  What the personnel module has to configure. One row per surface, each leading to its own
  page — see `<SettingsMenu>` for why the lists are not inlined here.

  Every row carries its OWN key rather than the module's: someone trusted to maintain the
  vocabulary of ID types, family relations or eligibilities is not thereby trusted to read
  employment records, and the reverse is the more common grant. That is also why this page is
  gated on holding ANY of them rather than on `personnel.read`.
*/
const ENTRIES: SettingsMenuEntry[] = [
  {
    key: 'identification',
    label: 'Identification',
    description:
      'The identification numbers a personnel record collects — SSS, GSIS, TIN, and whatever else this employer asks for.',
    icon: IdCard,
    to: '/personnel/settings/identification',
    permission: 'identification-types.read',
  },
  {
    /*
      The heavier consumer owns a catalog's one Settings menu, and that is Personnel: leave
      credits hang off an employee's profile, so the vocabulary they draw on is maintained
      beside the rest of what a 201 file reads.
    */
    key: 'leave-types',
    label: 'Leave Types',
    description:
      'The kinds of leave the organization grants, and how each one is tracked — a running credit balance, or a whole entitlement granted at once.',
    icon: CalendarClock,
    to: '/personnel/settings/leave-types',
    permission: 'leave-types.read',
  },
  {
    key: 'family-relations',
    label: 'Family Relations',
    description:
      'Who a family member can be to an employee — CS Form 212 asks for a spouse, both parents and the children, and a 201 file often needs more.',
    icon: Users,
    to: '/personnel/settings/family-relations',
    permission: 'family-relations.read',
  },
  {
    /*
      Moved here from Organization → Settings. A position's qualification standards still name
      an eligibility, so that menu had a claim on it — but section IV of CS Form 212 files what
      an employee EARNED under the same catalog, which is the heavier consumer, and one catalog
      reached from two menus is two breadcrumbs disagreeing about where the reader came from.

      Its own key, like the two rows above: someone trusted to maintain the vocabulary is not
      thereby trusted to read employment records.
    */
    key: 'eligibilities',
    label: 'Eligibilities',
    description:
      'The eligibilities this organization recognises — what an employee can be recorded as having earned, and what a position may require.',
    icon: BadgeCheck,
    to: '/personnel/settings/eligibilities',
    permission: 'eligibilities.read',
  },
  {
    /*
      Its own key, like every row above: someone trusted to maintain what the survey ASKS is not
      thereby trusted to read what employees ANSWERED — and the reverse is the more common
      grant. Recording an answer is `personnel.update`, which no key here implies.
    */
    key: 'survey',
    label: 'Survey',
    description:
      'The questions a 201 file is surveyed against — CS Form 212 asks items 34 to 40, and an employer often asks more.',
    icon: ClipboardList,
    to: '/personnel/settings/survey',
    permission: 'survey-questions.read',
  },
  {
    /*
      Its own key, like every row above: someone trusted to maintain the competency FRAMEWORK is
      not thereby trusted to read which employees were trained against it. Recording an L&D
      record is `personnel.update`, which no key here implies.
    */
    key: 'competencies',
    label: 'Competencies',
    description:
      'The competency framework an L&D record is measured against — what a training addresses, and the level it is meant to reach.',
    icon: Target,
    to: '/personnel/settings/competencies',
    permission: 'competencies.read',
  },

  /*
    The four L&D vocabularies, from the same descriptor the routes and the pages read — one
    fact spelled once, so a row cannot point at a path no route serves.

    They sit AFTER Competencies because they arrived after it and a menu appends rather than
    slotting in, which keeps the position of every row a reader has already learned. All four
    carry ONE key: maintaining these vocabularies is one administrative job, so somebody
    trusted with the intervention list is trusted with the type list.
  */
  ...LEARNING_DEVELOPMENT_CATALOGS.map((catalog): SettingsMenuEntry => ({
    key: catalog.slug,
    label: catalog.label,
    description: catalog.description,
    icon: catalog.icon,
    to: catalog.settingsPath,
    permission: 'learning-development.read',
  })),

  {
    /*
      Its own key, like every row above: someone trusted to decide what the organization asks
      EVERY employee for is not thereby trusted to read one employee's file — and the reverse
      is the more common grant. Filing a document against the checklist is `personnel.update`,
      which no key here implies.

      It sits after the L&D vocabularies because it arrived after them and a menu appends
      rather than slotting in, which keeps the position of every row a reader has already
      learned.
    */
    key: 'documents',
    label: 'Documents',
    description:
      'What a 201 file is expected to hold — birth certificate, marriage certificate, diploma, training certificates, appointments, NOSA.',
    icon: FolderOpen,
    to: '/personnel/settings/documents',
    permission: 'personnel-document-types.read',
  },

  {
    /*
      WHO READS WHAT. Not a vocabulary like every other row here — it is a mirror of user
      management, scoped to this module: the roster of which account sits on which rung of the
      clearance ladder, and nothing else about the account.

      It is here because the question "who can read our 201 files" is asked from inside
      Personnel, by somebody who should not have to open the whole account list to answer it.
      The LADDER itself is maintained under Administration — one ladder serves every gated
      module, and a second one maintained here would be two sets of rungs that disagree.

      Its own key, like every other row: `users.assign-levels` is what the page does, and it is
      the one key in the product that widens what a reader can see without touching a single
      permission.
    */
    key: 'access-levels',
    label: 'Access Levels',
    description:
      'Which personnel records each account can read. A record is classified at a level; an account is tagged with one, and reads that level and weaker.',
    icon: ShieldCheck,
    to: '/personnel/settings/access-levels',
    permission: 'users.assign-levels',
  },
];

/**
 * Personnel → Settings: a **menu of what the module configures**, not the settings themselves.
 *
 * The sidebar carries one entry per module rather than one per configurable list, and this
 * page is where that entry expands. Each row leads to a full page for that list, with its own
 * breadcrumb, toolbar and settings cog.
 */
export function PersonnelSettingsPage() {
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <Breadcrumb segments={['Human Resources', 'Personnel', 'Settings']} />

      <div className="modern-scrollbar min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          {/*
            One line, and it says the thing the menu cannot: where the records went. Without it
            a reader who came looking for employees reads this row and concludes the module has
            nothing else.
          */}
          <p className="text-xs text-slate-500">
            The vocabularies a personnel record draws on, and who may read which records.
            Employees themselves are created and maintained on 201 Files.
          </p>

          <SettingsMenu entries={ENTRIES} />
        </div>
      </div>
    </div>
  );
}
